/**
 * Sample-data generators for the QMD Generator.
 *
 * Each function takes (centerLat, centerLon, radiusMiles, seed) and returns a
 * GeoJSON FeatureCollection ready to feed into Leaflet's L.geoJSON layer.
 *
 * Deterministic: same seed + same center always produces identical output.
 * This matters because shareable demo URLs need to regenerate the same map
 * for every viewer.
 *
 * Each Feature has a `properties.atlasCategory` field used by the symbology
 * layer to pick the right icon/color. The categories are exhaustive per map
 * type and stable — do not rename without updating the symbology map in
 * src/components/MapSymbology.jsx.
 */

// ---------- seeded RNG (mulberry32) ----------

function hashSeed(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return (h ^= h >>> 16) >>> 0;
}

function mulberry32(seed) {
  let a = typeof seed === 'string' ? hashSeed(seed) : seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---------- geometry helpers ----------

const MILES_TO_DEG_LAT = 1 / 69; // ~constant
function milesToDegLon(lat, miles) {
  return miles / (69 * Math.cos((lat * Math.PI) / 180));
}

/**
 * Clamp (lon, lat) into a bbox [minLat, minLon, maxLat, maxLon]. Used to
 * keep every generated feature inside the selected city's envelope so
 * coastline cities (Alexandria/Miami/Charleston) don't spawn hospitals in
 * adjacent water. A no-op when bbox is null (custom-coord path).
 */
function clampBbox(lon, lat, bbox) {
  if (!bbox || bbox.length !== 4) return [lon, lat];
  const [minLat, minLon, maxLat, maxLon] = bbox;
  return [
    Math.max(minLon, Math.min(maxLon, lon)),
    Math.max(minLat, Math.min(maxLat, lat)),
  ];
}

function jitterPoint(lat, lon, radiusMiles, rng, bbox) {
  const angle = rng() * 2 * Math.PI;
  const dist = Math.sqrt(rng()) * radiusMiles; // sqrt for uniform area distribution
  const p = [
    lon + Math.cos(angle) * milesToDegLon(lat, dist),
    lat + Math.sin(angle) * dist * MILES_TO_DEG_LAT,
  ];
  return clampBbox(p[0], p[1], bbox);
}

function clusterPoint(centerLat, centerLon, spreadMiles, rng, bbox) {
  const angle = rng() * 2 * Math.PI;
  const dist = rng() * spreadMiles;
  const p = [
    centerLon + Math.cos(angle) * milesToDegLon(centerLat, dist),
    centerLat + Math.sin(angle) * dist * MILES_TO_DEG_LAT,
  ];
  return clampBbox(p[0], p[1], bbox);
}

function makePolygon(centerLat, centerLon, radiusMilesLat, radiusMilesLon, rng, sides = 8) {
  const ring = [];
  const rotation = rng() * Math.PI;
  for (let i = 0; i < sides; i++) {
    const angle = (i / sides) * 2 * Math.PI + rotation;
    const wobble = 0.7 + rng() * 0.5;
    ring.push([
      centerLon + Math.cos(angle) * milesToDegLon(centerLat, radiusMilesLon * wobble),
      centerLat + Math.sin(angle) * radiusMilesLat * wobble * MILES_TO_DEG_LAT,
    ]);
  }
  ring.push(ring[0]);
  return ring;
}

// ---------- 1. CRASH SAFETY ----------

export function generateCrashSafety(centerLat, centerLon, radiusMiles = 2, seed = 'default', bbox = null) {
  const rng = mulberry32(seed + ':crash');
  const features = [];

  // Scale hotspot polygons + cluster spread proportionally with radius
  // so the slider visibly resizes the polygons, not just the spacing
  // between them.
  const hotspotR = radiusMiles * 0.12; // hotspot polygon "radius" in miles
  const clusterR = radiusMiles * 0.075; // crash-cluster spread in miles

  // 2-3 hotspot clusters
  const hotspots = [];
  const hotspotCount = 2 + Math.floor(rng() * 2);
  for (let i = 0; i < hotspotCount; i++) {
    const center = jitterPoint(centerLat, centerLon, radiusMiles * 0.7, rng, bbox);
    hotspots.push({ lon: center[0], lat: center[1], severity: i === 0 ? 'fatal' : 'injury' });

    // Hotspot polygon — scaled with radius
    features.push({
      type: 'Feature',
      properties: {
        atlasCategory: 'crash-hotspot',
        severity: hotspots[i].severity,
        label: `Hotspot ${String.fromCharCode(65 + i)}`,
      },
      geometry: {
        type: 'Polygon',
        coordinates: [
          makePolygon(center[1], center[0], hotspotR, hotspotR * 0.72, rng, 10),
        ],
      },
    });

    // Cluster of crashes inside hotspot — scaled spread
    const clusterSize = 8 + Math.floor(rng() * 6);
    for (let j = 0; j < clusterSize; j++) {
      const pt = clusterPoint(center[1], center[0], clusterR, rng, bbox);
      features.push({
        type: 'Feature',
        properties: {
          atlasCategory: 'crash-point',
          severity: rng() < 0.15 ? 'fatal' : rng() < 0.5 ? 'injury' : 'property',
          year: 2023 + Math.floor(rng() * 3),
        },
        geometry: { type: 'Point', coordinates: pt },
      });
    }
  }

  // Scattered background crashes
  const scatterCount = 12 + Math.floor(rng() * 10);
  for (let i = 0; i < scatterCount; i++) {
    features.push({
      type: 'Feature',
      properties: {
        atlasCategory: 'crash-point',
        severity: rng() < 0.05 ? 'fatal' : rng() < 0.35 ? 'injury' : 'property',
        year: 2023 + Math.floor(rng() * 3),
      },
      geometry: { type: 'Point', coordinates: jitterPoint(centerLat, centerLon, radiusMiles, rng, bbox) },
    });
  }

  const total = features.filter((f) => f.properties.atlasCategory === 'crash-point').length;
  const fatal = features.filter(
    (f) => f.properties.atlasCategory === 'crash-point' && f.properties.severity === 'fatal',
  ).length;

  return {
    type: 'FeatureCollection',
    features,
    metadata: {
      mapType: 'crash_safety',
      stats: {
        total_incidents: total,
        hotspot_count: hotspotCount,
        fatal_count: fatal,
        area_sq_mi: +(Math.PI * radiusMiles * radiusMiles).toFixed(1),
      },
      legend: [
        { label: 'Severe / fatal', color: '#C72B1A', shape: 'circle' },
        { label: 'Injury', color: '#E89622', shape: 'circle' },
        { label: 'Property damage', color: '#F5C236', shape: 'circle' },
        { label: 'High-risk zone', color: '#C72B1A', shape: 'polygon', opacity: 0.22 },
      ],
    },
  };
}

// ---------- 2. EVENT OPERATIONS ----------

export function generateEventOps(centerLat, centerLon, radiusMiles = 0.5, seed = 'default', bbox = null) {
  const rng = mulberry32(seed + ':event');
  const features = [];

  // Event boundary
  features.push({
    type: 'Feature',
    properties: { atlasCategory: 'event-boundary', label: 'Event footprint' },
    geometry: {
      type: 'Polygon',
      coordinates: [makePolygon(centerLat, centerLon, radiusMiles * 0.6, radiusMiles * 0.7, rng, 6)],
    },
  });

  // 4 gates around perimeter
  for (let i = 0; i < 4; i++) {
    const angle = (i / 4) * 2 * Math.PI + Math.PI / 4;
    const lon = centerLon + Math.cos(angle) * milesToDegLon(centerLat, radiusMiles * 0.6);
    const lat = centerLat + Math.sin(angle) * radiusMiles * 0.55 * MILES_TO_DEG_LAT;
    features.push({
      type: 'Feature',
      properties: { atlasCategory: 'event-gate', label: `Gate ${i + 1}`, type: i === 0 ? 'main' : 'secondary' },
      geometry: { type: 'Point', coordinates: [lon, lat] },
    });
  }

  // 3 medical stations
  for (let i = 0; i < 3; i++) {
    features.push({
      type: 'Feature',
      properties: { atlasCategory: 'event-medical', label: `Medical ${i + 1}`, capacity: 25 + Math.floor(rng() * 30) },
      geometry: { type: 'Point', coordinates: jitterPoint(centerLat, centerLon, radiusMiles * 0.35, rng, bbox) },
    });
  }

  // 6 security posts
  for (let i = 0; i < 6; i++) {
    features.push({
      type: 'Feature',
      properties: { atlasCategory: 'event-security', label: `Post ${i + 1}` },
      geometry: { type: 'Point', coordinates: jitterPoint(centerLat, centerLon, radiusMiles * 0.5, rng, bbox) },
    });
  }

  // 2 vendor zones
  for (let i = 0; i < 2; i++) {
    const c = jitterPoint(centerLat, centerLon, radiusMiles * 0.4, rng, bbox);
    features.push({
      type: 'Feature',
      properties: { atlasCategory: 'event-vendor', label: `Vendor zone ${i + 1}` },
      geometry: {
        type: 'Polygon',
        coordinates: [makePolygon(c[1], c[0], 0.04, 0.05, rng, 5)],
      },
    });
  }

  return {
    type: 'FeatureCollection',
    features,
    metadata: {
      mapType: 'event_ops',
      stats: {
        capacity_estimate: 8000 + Math.floor(rng() * 12000),
        gate_count: 4,
        medical_capacity: 85,
        vendor_zones: 2,
      },
      legend: [
        { label: 'Event boundary', color: '#0B1F3A', shape: 'polygon', opacity: 0.15 },
        { label: 'Gate', color: '#D4A437', shape: 'circle' },
        { label: 'Medical station', color: '#C72B1A', shape: 'cross' },
        { label: 'Security post', color: '#0B1F3A', shape: 'square' },
        { label: 'Vendor zone', color: '#2A6AB8', shape: 'polygon', opacity: 0.2 },
      ],
    },
  };
}

// ---------- 3. PERMIT / ROW ----------

export function generatePermitROW(centerLat, centerLon, radiusMiles = 1, seed = 'default', bbox = null) {
  const rng = mulberry32(seed + ':permit');
  const features = [];

  // ROW corridor — a linear-ish polygon running roughly east-west
  const angle = (rng() - 0.5) * Math.PI / 4; // -22 to +22 degrees
  const length = radiusMiles * 1.4;
  const halfWidth = 0.04;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const dx = milesToDegLon(centerLat, length / 2);
  const dy = (length / 2) * MILES_TO_DEG_LAT;
  const wx = milesToDegLon(centerLat, halfWidth);
  const wy = halfWidth * MILES_TO_DEG_LAT;

  const corridor = [
    [centerLon - dx * cos - wx * sin, centerLat - dy * sin + wy * cos],
    [centerLon + dx * cos - wx * sin, centerLat + dy * sin + wy * cos],
    [centerLon + dx * cos + wx * sin, centerLat + dy * sin - wy * cos],
    [centerLon - dx * cos + wx * sin, centerLat - dy * sin - wy * cos],
  ];
  corridor.push(corridor[0]);

  features.push({
    type: 'Feature',
    properties: { atlasCategory: 'row-corridor', label: 'Project corridor', length_mi: +length.toFixed(2) },
    geometry: { type: 'Polygon', coordinates: [corridor] },
  });

  // Affected parcels along corridor
  const parcelCount = 8 + Math.floor(rng() * 7);
  for (let i = 0; i < parcelCount; i++) {
    const t = (i + 0.5) / parcelCount;
    const side = i % 2 === 0 ? 1 : -1;
    const cx = centerLon - dx * cos + (2 * dx * cos) * t + side * 0.0008;
    const cy = centerLat - dy * sin + (2 * dy * sin) * t + side * 0.0008;
    features.push({
      type: 'Feature',
      properties: {
        atlasCategory: 'parcel-affected',
        parcel_id: `P-${1000 + i}`,
        owner: `Owner ${i + 1}`,
      },
      geometry: { type: 'Polygon', coordinates: [makePolygon(cy, cx, 0.025, 0.03, rng, 4)] },
    });
  }

  // Permit application points
  const permitCount = 3 + Math.floor(rng() * 3);
  for (let i = 0; i < permitCount; i++) {
    const t = (i + 0.5) / permitCount;
    features.push({
      type: 'Feature',
      properties: {
        atlasCategory: 'permit-point',
        permit_id: `PRM-2026-${100 + i}`,
        status: rng() < 0.5 ? 'pending' : 'approved',
      },
      geometry: {
        type: 'Point',
        coordinates: [
          centerLon - dx * cos + (2 * dx * cos) * t,
          centerLat - dy * sin + (2 * dy * sin) * t,
        ],
      },
    });
  }

  // Utility easement (dashed line)
  features.push({
    type: 'Feature',
    properties: { atlasCategory: 'easement-utility', label: 'Power easement' },
    geometry: {
      type: 'LineString',
      coordinates: [
        [centerLon - dx * cos, centerLat - dy * sin + 0.003],
        [centerLon + dx * cos, centerLat + dy * sin + 0.003],
      ],
    },
  });

  return {
    type: 'FeatureCollection',
    features,
    metadata: {
      mapType: 'permit_row',
      stats: {
        parcels_affected: parcelCount,
        row_length_mi: +length.toFixed(2),
        permit_count: permitCount,
        easements: 1,
      },
      legend: [
        { label: 'ROW corridor', color: '#D4A437', shape: 'polygon', opacity: 0.4 },
        { label: 'Affected parcel', color: '#C72B1A', shape: 'polygon', opacity: 0.35 },
        { label: 'Permit application', color: '#0B1F3A', shape: 'diamond' },
        { label: 'Utility easement', color: '#2A6AB8', shape: 'dashed-line' },
      ],
    },
  };
}

// ---------- 4. EMERGENCY RESPONSE ----------

export function generateEmergencyResponse(centerLat, centerLon, radiusMiles = 5, seed = 'default', bbox = null) {
  const rng = mulberry32(seed + ':emergency');
  const features = [];

  // EOC at center
  features.push({
    type: 'Feature',
    properties: { atlasCategory: 'emergency-eoc', label: 'Emergency Operations Center' },
    geometry: { type: 'Point', coordinates: [centerLon, centerLat] },
  });

  // Fire/EMS stations
  const stationCount = 3 + Math.floor(rng() * 3);
  for (let i = 0; i < stationCount; i++) {
    features.push({
      type: 'Feature',
      properties: {
        atlasCategory: rng() < 0.6 ? 'emergency-fire' : 'emergency-ems',
        label: `Station ${i + 1}`,
        district: `District ${i + 1}`,
      },
      geometry: { type: 'Point', coordinates: jitterPoint(centerLat, centerLon, radiusMiles * 0.7, rng, bbox) },
    });
  }

  // Hospitals
  const hospCount = 1 + Math.floor(rng() * 2);
  for (let i = 0; i < hospCount; i++) {
    features.push({
      type: 'Feature',
      properties: {
        atlasCategory: 'emergency-hospital',
        label: i === 0 ? 'Regional Medical Center' : `Hospital ${i + 1}`,
        beds: 200 + Math.floor(rng() * 300),
        trauma_level: i === 0 ? 'I' : 'II',
      },
      geometry: { type: 'Point', coordinates: jitterPoint(centerLat, centerLon, radiusMiles * 0.5, rng, bbox) },
    });
  }

  // Shelters
  const shelterCount = 2 + Math.floor(rng() * 2);
  for (let i = 0; i < shelterCount; i++) {
    const c = jitterPoint(centerLat, centerLon, radiusMiles * 0.6, rng, bbox);
    features.push({
      type: 'Feature',
      properties: {
        atlasCategory: 'emergency-shelter',
        label: `Shelter ${i + 1}`,
        capacity: 150 + Math.floor(rng() * 350),
      },
      geometry: { type: 'Polygon', coordinates: [makePolygon(c[1], c[0], 0.05, 0.06, rng, 4)] },
    });
  }

  // Hazard zone (flood or wildfire — pick one per generation)
  const hazardType = rng() < 0.6 ? 'flood' : 'wildfire';
  const hc = jitterPoint(centerLat, centerLon, radiusMiles * 0.4, rng, bbox);
  features.push({
    type: 'Feature',
    properties: { atlasCategory: `hazard-${hazardType}`, label: `${hazardType[0].toUpperCase() + hazardType.slice(1)} hazard zone` },
    geometry: { type: 'Polygon', coordinates: [makePolygon(hc[1], hc[0], radiusMiles * 0.25, radiusMiles * 0.3, rng, 12)] },
  });

  return {
    type: 'FeatureCollection',
    features,
    metadata: {
      mapType: 'emergency_response',
      stats: {
        response_assets: 1 + stationCount + hospCount,
        shelter_capacity: shelterCount * 250,
        hazard_type: hazardType,
        coverage_sq_mi: +(Math.PI * radiusMiles * radiusMiles).toFixed(1),
      },
      legend: [
        { label: 'EOC', color: '#D4A437', shape: 'star' },
        { label: 'Fire station', color: '#C72B1A', shape: 'square' },
        { label: 'EMS station', color: '#2A6AB8', shape: 'square' },
        { label: 'Hospital', color: '#1D9E75', shape: 'cross' },
        { label: 'Shelter', color: '#1D9E75', shape: 'polygon', opacity: 0.3 },
        { label: `${hazardType} hazard`, color: hazardType === 'flood' ? '#2A6AB8' : '#C72B1A', shape: 'polygon', opacity: 0.25 },
      ],
    },
  };
}

// ---------- public registry ----------

export const MAP_TYPES = {
  crash_safety: {
    label: 'Crash safety',
    description: 'Crash hotspots, severity-coded incidents, and high-risk zones.',
    generator: generateCrashSafety,
    defaultRadiusMiles: 2,
  },
  event_ops: {
    label: 'Event operations',
    description: 'Event footprint with gates, medical, security, and vendor zones.',
    generator: generateEventOps,
    defaultRadiusMiles: 0.5,
  },
  permit_row: {
    label: 'Permit / right-of-way',
    description: 'Project corridor with affected parcels, permits, and easements.',
    generator: generatePermitROW,
    defaultRadiusMiles: 1,
  },
  emergency_response: {
    label: 'Emergency response',
    description: 'Response assets, shelters, and hazard zones across a region.',
    generator: generateEmergencyResponse,
    defaultRadiusMiles: 5,
  },
};

export function generateForMapType(mapType, centerLat, centerLon, seed, bbox = null) {
  const cfg = MAP_TYPES[mapType];
  if (!cfg) throw new Error(`Unknown map type: ${mapType}`);
  return cfg.generator(centerLat, centerLon, cfg.defaultRadiusMiles, seed, bbox);
}
